/**
 * Helper that merges the hardcoded Sage warehouse list with the live
 * item-count data returned by `/portal/orders/warehouses`.
 *
 * Rules:
 *   1. Every hardcoded warehouse appears, in its original order.
 *   2. If the API reports item counts for a hardcoded code, they are
 *      rendered as the trailing meta text (e.g. "1,234 items").
 *   3. Any warehouse code the API sees but the hardcoded list doesn't know
 *      about is appended as a non-highlighted "Other" entry with a generic
 *      label so users can still pick it.
 */
import type { WarehouseOption } from '../services/portalOrders';
import type { DropdownItem } from '../components/portal/SearchableDropdown';
import { SAGE_WAREHOUSES } from './sageWarehouses';

function formatItemCount(count: number): string {
  return `${count.toLocaleString()} item${count === 1 ? '' : 's'}`;
}

export function buildWarehouseDropdownItems(
  apiOptions: readonly WarehouseOption[],
): DropdownItem[] {
  const countByCode = new Map<string, number>();
  for (const o of apiOptions) {
    countByCode.set(o.warehouse_code.toUpperCase(), o.item_count);
  }

  const items: DropdownItem[] = SAGE_WAREHOUSES.map((w) => {
    const count = countByCode.get(w.code.toUpperCase());
    // Prefer the live item count when we have it; otherwise tag inactive
    // warehouses so users understand why they're in the expanded group.
    let meta: string | undefined;
    if (count !== undefined) {
      meta = formatItemCount(count);
    } else if (w.inactive) {
      meta = 'Inactive';
    }
    return {
      value: w.code,
      label: w.label,
      highlighted: w.highlighted,
      meta,
    };
  });

  // Append any codes the API saw that aren't in the hardcoded list.
  const knownCodes = new Set(SAGE_WAREHOUSES.map((w) => w.code.toUpperCase()));
  for (const o of apiOptions) {
    const upper = o.warehouse_code.toUpperCase();
    if (!knownCodes.has(upper)) {
      items.push({
        value: o.warehouse_code,
        label: '(unknown code)',
        highlighted: false,
        meta: formatItemCount(o.item_count),
      });
    }
  }

  return items;
}
